/**
 * The matrix's URL: ?mode=dishes|recipes|menu|custom&menuId=&d=&r=&i=.
 *
 * Search params may arrive as a JS value (an in-app navigate or Link) or as a
 * round-tripped query string (a typed URL, a refresh, a shared link), so both
 * shapes are accepted rather than trusting the router's own codec.
 *
 * Every field is optional in the parsed result, so a plain `<Link
 * to="/allergens">` elsewhere in the app type-checks; the screen fills in the
 * route's default mode.
 */
import type { MatrixMode } from '@/api/queries/allergens';

export const MODES: MatrixMode[] = ['recipes', 'dishes', 'menu', 'custom'];

export type MatrixSearch = {
  mode?: MatrixMode;
  menuId?: number;
  d?: number[];
  r?: number[];
  i?: number[];
};

export type PickSearch = Pick<MatrixSearch, 'd' | 'r' | 'i'>;

function toId(v: unknown): number | undefined {
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

function toIds(v: unknown): number[] | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [v];
  const ids = raw.map(toId).filter((n): n is number => n !== undefined);
  return ids.length ? [...new Set(ids)] : undefined;
}

export function parsePicks(s: Record<string, unknown>): PickSearch {
  return { d: toIds(s.d), r: toIds(s.r), i: toIds(s.i) };
}

export function parseMatrixSearch(s: Record<string, unknown>): MatrixSearch {
  const mode = MODES.includes(s.mode as MatrixMode) ? (s.mode as MatrixMode) : undefined;
  return { mode, menuId: toId(s.menuId), ...parsePicks(s) };
}

/** A saved matrix's URL: ?cell=<recipe id>-<allergen id> opens that cell.
 *  A hyphen, not a dot, so the router's own parse never reads "12.40" as
 *  the number 12.4 and loses the allergen. */
export type SavedSearch = { cell?: string };

export function parseCellPick(v: unknown): string | undefined {
  const raw = typeof v === 'string' ? v : '';
  return /^[1-9]\d*-[1-9]\d*$/.test(raw) ? raw : undefined;
}

export function cellPick(recipeId: number, allergenId: number): string {
  return `${recipeId}-${allergenId}`;
}

/** "12-4" as its two ids. */
export function cellPickIds(cell: string | undefined): { recipeId: number; allergenId: number } | null {
  const raw = parseCellPick(cell);
  if (!raw) return null;
  const [recipeId, allergenId] = raw.split('-').map(Number);
  return { recipeId, allergenId };
}
